import {
  createTaskEditorBlock,
  createTaskEditorButtonGroupOption,
  createTaskEditorLine,
  getTaskEditorButtonGroupBranchTargetBlocks,
  getTaskEditorButtonGroupOptions,
  isTaskEditorChoiceCellType,
  isTaskEditorLastCardBlock,
  isTaskEditorNestedBlockType,
  normalizeTaskEditorButtonGroupOption,
  TASK_EDITOR_LAST_CARD_TYPE,
  TaskEditorBlock,
  TaskEditorBlockType,
  TaskEditorCellType,
  TaskEditorTypographyStyle,
} from "./taskEditorUtils";
import { createEmptyTaskEditorState } from "./taskEditorDiff";

export function applyCellTypeChange(
  blocks: TaskEditorBlock[],
  blockId: string,
  lineId: string,
  _cellId: string,
  type: TaskEditorCellType | null
): TaskEditorBlock[] {
  if (!type) {
    return blocks;
  }

  return blocks.map((block) => {
    if (block.id !== blockId || !isTaskEditorNestedBlockType(block.type)) {
      return block;
    }

    return {
      ...block,
      lines: (block.lines ?? []).map((line) => {
        if (line.id !== lineId) {
          return line;
        }

        return {
          ...createTaskEditorLine(type),
          id: line.id,
          content: line.content,
          ...(isTaskEditorChoiceCellType(type) &&
          isTaskEditorChoiceCellType(line.type)
            ? {
                options: getTaskEditorButtonGroupOptions(line.options).map(
                  (option) => normalizeTaskEditorButtonGroupOption(option)
                ),
              }
            : {}),
          ...(type === "Quiz" && line.type === "Quiz"
            ? {
                incorrectMessage: line.incorrectMessage ?? "",
              }
            : {}),
          ...(type === "ButtonSelection" && line.type === "ButtonSelection"
            ? {
                allowMultipleSelection: line.allowMultipleSelection !== false,
              }
            : {}),
          ...((type === "Textarea" || type === "Preset") &&
          (line.type === "Textarea" || line.type === "Preset")
            ? {
                textareaPlaceholder: line.textareaPlaceholder ?? "",
              }
            : {}),
        };
      }),
    };
  });
}

export function applyBlockTypeChange(
  blocks: TaskEditorBlock[],
  blockId: string,
  nextType: TaskEditorBlockType
): TaskEditorBlock[] {
  const currentBlock = blocks.find((block) => block.id === blockId);

  if (
    !currentBlock ||
    isTaskEditorLastCardBlock(currentBlock) ||
    nextType === TASK_EDITOR_LAST_CARD_TYPE ||
    currentBlock.type === nextType
  ) {
    return blocks;
  }

  return blocks.map((block) =>
    block.id === blockId
      ? {
          ...block,
          type: nextType,
          ...(isTaskEditorNestedBlockType(nextType)
            ? {
                lines:
                  block.lines && block.lines.length > 0
                    ? block.lines
                    : [createTaskEditorLine()],
              }
            : {}),
        }
      : block
  );
}

export function applyBlockContentChange(
  blocks: TaskEditorBlock[],
  blockId: string,
  content: string
): TaskEditorBlock[] {
  const currentBlock = blocks.find((block) => block.id === blockId);

  if (
    !currentBlock ||
    isTaskEditorLastCardBlock(currentBlock) ||
    currentBlock.content === content
  ) {
    return blocks;
  }

  return blocks.map((block) =>
    block.id === blockId ? { ...block, content } : block
  );
}

export function applyAddBlock(
  blocks: TaskEditorBlock[],
  type: TaskEditorBlockType,
  insertionIndex?: number
): TaskEditorBlock[] {
  if (type === TASK_EDITOR_LAST_CARD_TYPE) {
    return blocks;
  }

  const nextBlocks = [...blocks];
  const editableBlockIndexes = blocks.reduce<number[]>(
    (indexes, block, index) => {
      if (!isTaskEditorLastCardBlock(block)) {
        indexes.push(index);
      }

      return indexes;
    },
    []
  );
  const normalizedInsertionIndex =
    typeof insertionIndex === "number" && Number.isFinite(insertionIndex)
      ? Math.max(0, Math.min(insertionIndex, editableBlockIndexes.length))
      : editableBlockIndexes.length;
  const targetArrayIndex =
    normalizedInsertionIndex >= editableBlockIndexes.length
      ? (() => {
          const lastCardIndex = blocks.findIndex(isTaskEditorLastCardBlock);
          return lastCardIndex === -1 ? blocks.length : lastCardIndex;
        })()
      : editableBlockIndexes[normalizedInsertionIndex];

  nextBlocks.splice(targetArrayIndex, 0, createTaskEditorBlock(type));
  return nextBlocks;
}

export function applyAddLine(
  blocks: TaskEditorBlock[],
  blockId: string
): TaskEditorBlock[] {
  return blocks.map((block) =>
    block.id === blockId && isTaskEditorNestedBlockType(block.type)
      ? {
          ...block,
          lines: [...(block.lines ?? []), createTaskEditorLine()],
        }
      : block
  );
}

export function applyRemoveLine(
  blocks: TaskEditorBlock[],
  blockId: string,
  lineId: string
): TaskEditorBlock[] {
  return blocks.map((block) => {
    if (block.id !== blockId || !isTaskEditorNestedBlockType(block.type)) {
      return block;
    }

    const nextLines = (block.lines ?? []).filter((line) => line.id !== lineId);

    return {
      ...block,
      lines: nextLines.length > 0 ? nextLines : [createTaskEditorLine()],
    };
  });
}

export function applyContentLineCellChange(
  blocks: TaskEditorBlock[],
  blockId: string,
  lineId: string,
  _cellId: string,
  content: string
): TaskEditorBlock[] {
  const currentBlock = blocks.find((block) => block.id === blockId);
  const currentLine = currentBlock?.lines?.find((line) => line.id === lineId);

  if (
    !currentBlock ||
    !isTaskEditorNestedBlockType(currentBlock.type) ||
    !currentLine ||
    currentLine.content === content
  ) {
    return blocks;
  }

  return blocks.map((block) => {
    if (block.id !== blockId || !isTaskEditorNestedBlockType(block.type)) {
      return block;
    }

    return {
      ...block,
      lines: (block.lines ?? []).map((line) =>
        line.id === lineId
          ? {
              ...line,
              content,
            }
          : line
      ),
    };
  });
}

export function applyButtonGroupOptionChange(
  blocks: TaskEditorBlock[],
  blockId: string,
  lineId: string,
  optionIndex: number,
  value: string
): TaskEditorBlock[] {
  const currentBlock = blocks.find((block) => block.id === blockId);
  const currentLine = currentBlock?.lines?.find((line) => line.id === lineId);
  const isChoiceLine = currentLine
    ? isTaskEditorChoiceCellType(currentLine.type)
    : false;
  const currentOptions = isChoiceLine
    ? getTaskEditorButtonGroupOptions(currentLine?.options)
    : undefined;

  if (
    !currentBlock ||
    !isTaskEditorNestedBlockType(currentBlock.type) ||
    !isChoiceLine ||
    !currentOptions ||
    optionIndex < 0 ||
    optionIndex >= currentOptions.length ||
    currentOptions[optionIndex].text === value
  ) {
    return blocks;
  }

  return blocks.map((block) => {
    if (block.id !== blockId || !isTaskEditorNestedBlockType(block.type)) {
      return block;
    }

    return {
      ...block,
      lines: (block.lines ?? []).map((line) => {
        if (line.id !== lineId || !isTaskEditorChoiceCellType(line.type)) {
          return line;
        }

        const nextOptions = getTaskEditorButtonGroupOptions(line.options).map(
          (option) => normalizeTaskEditorButtonGroupOption(option)
        );
        nextOptions[optionIndex] = createTaskEditorButtonGroupOption({
          ...nextOptions[optionIndex],
          text: value,
        });

        return {
          ...line,
          options: nextOptions,
        };
      }),
    };
  });
}

export function applyButtonGroupOptionBranchChange(
  blocks: TaskEditorBlock[],
  blockId: string,
  lineId: string,
  optionIndex: number,
  key: "showFromId" | "showToId" | "restartFromId" | "hideFromId",
  value: string
): TaskEditorBlock[] {
  const currentBlock = blocks.find((block) => block.id === blockId);
  const currentLine = currentBlock?.lines?.find((line) => line.id === lineId);
  const currentOptions =
    currentLine?.type === "ButtonGroup"
      ? getTaskEditorButtonGroupOptions(currentLine.options)
      : undefined;
  const normalizedValue = value.trim();
  const requestedValue =
    normalizedValue.length > 0 ? normalizedValue : undefined;
  const branchTargetBlocks = getTaskEditorButtonGroupBranchTargetBlocks(
    blocks,
    blockId
  );
  const isValidBranchValue =
    !requestedValue ||
    branchTargetBlocks.some(({ block }) => block.id === requestedValue);
  const nextBranchValue = isValidBranchValue ? requestedValue : undefined;
  const currentOptionValue =
    currentOptions && optionIndex >= 0 && optionIndex < currentOptions.length
      ? currentOptions[optionIndex][key]
      : undefined;

  if (
    !currentBlock ||
    !isTaskEditorNestedBlockType(currentBlock.type) ||
    currentLine?.type !== "ButtonGroup" ||
    !currentOptions ||
    optionIndex < 0 ||
    optionIndex >= currentOptions.length ||
    currentOptionValue === nextBranchValue
  ) {
    return blocks;
  }

  return blocks.map((block) => {
    if (block.id !== blockId || !isTaskEditorNestedBlockType(block.type)) {
      return block;
    }

    return {
      ...block,
      lines: (block.lines ?? []).map((line) => {
        if (line.id !== lineId || line.type !== "ButtonGroup") {
          return line;
        }

        const nextOptions = getTaskEditorButtonGroupOptions(line.options).map(
          (option) => normalizeTaskEditorButtonGroupOption(option)
        );
        const targetOption = nextOptions[optionIndex];
        nextOptions[optionIndex] = createTaskEditorButtonGroupOption({
          ...targetOption,
          [key]: nextBranchValue,
        });

        return {
          ...line,
          options: nextOptions,
        };
      }),
    };
  });
}

export function applyAddButtonGroupOption(
  blocks: TaskEditorBlock[],
  blockId: string,
  lineId: string
): TaskEditorBlock[] {
  const currentBlock = blocks.find((block) => block.id === blockId);
  const currentLine = currentBlock?.lines?.find((line) => line.id === lineId);

  if (
    !currentBlock ||
    !isTaskEditorNestedBlockType(currentBlock.type) ||
    !isTaskEditorChoiceCellType(currentLine?.type)
  ) {
    return blocks;
  }

  return blocks.map((block) => {
    if (block.id !== blockId || !isTaskEditorNestedBlockType(block.type)) {
      return block;
    }

    return {
      ...block,
      lines: (block.lines ?? []).map((line) => {
        if (line.id !== lineId || !isTaskEditorChoiceCellType(line.type)) {
          return line;
        }

        return {
          ...line,
          options: [
            ...getTaskEditorButtonGroupOptions(line.options).map((option) =>
              normalizeTaskEditorButtonGroupOption(option)
            ),
            createTaskEditorButtonGroupOption(),
          ],
        };
      }),
    };
  });
}

export function applyRemoveButtonGroupOption(
  blocks: TaskEditorBlock[],
  blockId: string,
  lineId: string,
  optionIndex: number
): TaskEditorBlock[] {
  const currentBlock = blocks.find((block) => block.id === blockId);
  const currentLine = currentBlock?.lines?.find((line) => line.id === lineId);
  const isChoiceLine = currentLine
    ? isTaskEditorChoiceCellType(currentLine.type)
    : false;
  const currentOptions = isChoiceLine
    ? getTaskEditorButtonGroupOptions(currentLine?.options)
    : undefined;

  if (
    !currentBlock ||
    !isTaskEditorNestedBlockType(currentBlock.type) ||
    !isChoiceLine ||
    !currentOptions ||
    optionIndex < 0 ||
    optionIndex >= currentOptions.length
  ) {
    return blocks;
  }

  return blocks.map((block) => {
    if (block.id !== blockId || !isTaskEditorNestedBlockType(block.type)) {
      return block;
    }

    return {
      ...block,
      lines: (block.lines ?? []).map((line) => {
        if (line.id !== lineId || !isTaskEditorChoiceCellType(line.type)) {
          return line;
        }

        const baseOptions = getTaskEditorButtonGroupOptions(line.options).map(
          (option) => normalizeTaskEditorButtonGroupOption(option)
        );
        const nextOptions = baseOptions.filter(
          (_option, index) => index !== optionIndex
        );

        return {
          ...line,
          options:
            nextOptions.length > 0
              ? nextOptions
              : [createTaskEditorButtonGroupOption()],
        };
      }),
    };
  });
}

export function applyQuizOptionCorrectToggle(
  blocks: TaskEditorBlock[],
  blockId: string,
  lineId: string,
  optionIndex: number,
  checked: boolean
): TaskEditorBlock[] {
  const currentBlock = blocks.find((block) => block.id === blockId);
  const currentLine = currentBlock?.lines?.find((line) => line.id === lineId);
  const currentOptions =
    currentLine?.type === "Quiz"
      ? getTaskEditorButtonGroupOptions(currentLine.options)
      : undefined;
  const currentValue =
    currentOptions && optionIndex >= 0 && optionIndex < currentOptions.length
      ? currentOptions[optionIndex].isCorrect === true
      : undefined;

  if (
    !currentBlock ||
    !isTaskEditorNestedBlockType(currentBlock.type) ||
    currentLine?.type !== "Quiz" ||
    !currentOptions ||
    optionIndex < 0 ||
    optionIndex >= currentOptions.length ||
    currentValue === checked
  ) {
    return blocks;
  }

  return blocks.map((block) => {
    if (block.id !== blockId || !isTaskEditorNestedBlockType(block.type)) {
      return block;
    }

    return {
      ...block,
      lines: (block.lines ?? []).map((line) => {
        if (line.id !== lineId || line.type !== "Quiz") {
          return line;
        }

        const nextOptions = getTaskEditorButtonGroupOptions(line.options).map(
          (option) => normalizeTaskEditorButtonGroupOption(option)
        );
        nextOptions[optionIndex] = createTaskEditorButtonGroupOption({
          ...nextOptions[optionIndex],
          isCorrect: checked,
        });

        return {
          ...line,
          options: nextOptions,
        };
      }),
    };
  });
}

export function applyQuizIncorrectMessageChange(
  blocks: TaskEditorBlock[],
  blockId: string,
  lineId: string,
  value: string
): TaskEditorBlock[] {
  const currentBlock = blocks.find((block) => block.id === blockId);
  const currentLine = currentBlock?.lines?.find((line) => line.id === lineId);

  if (
    !currentBlock ||
    !isTaskEditorNestedBlockType(currentBlock.type) ||
    currentLine?.type !== "Quiz" ||
    (currentLine.incorrectMessage ?? "") === value
  ) {
    return blocks;
  }

  return blocks.map((block) => {
    if (block.id !== blockId || !isTaskEditorNestedBlockType(block.type)) {
      return block;
    }

    return {
      ...block,
      lines: (block.lines ?? []).map((line) =>
        line.id === lineId && line.type === "Quiz"
          ? {
              ...line,
              incorrectMessage: value,
            }
          : line
      ),
    };
  });
}

export function applyButtonSelectionAllowMultipleChange(
  blocks: TaskEditorBlock[],
  blockId: string,
  lineId: string,
  checked: boolean
): TaskEditorBlock[] {
  const currentBlock = blocks.find((block) => block.id === blockId);
  const currentLine = currentBlock?.lines?.find((line) => line.id === lineId);
  const currentValue =
    currentLine?.type === "ButtonSelection"
      ? currentLine.allowMultipleSelection !== false
      : undefined;

  if (
    !currentBlock ||
    !isTaskEditorNestedBlockType(currentBlock.type) ||
    currentLine?.type !== "ButtonSelection" ||
    currentValue === checked
  ) {
    return blocks;
  }

  return blocks.map((block) => {
    if (block.id !== blockId || !isTaskEditorNestedBlockType(block.type)) {
      return block;
    }

    return {
      ...block,
      lines: (block.lines ?? []).map((line) =>
        line.id === lineId && line.type === "ButtonSelection"
          ? {
              ...line,
              allowMultipleSelection: checked,
            }
          : line
      ),
    };
  });
}

export function applyTextareaPlaceholderChange(
  blocks: TaskEditorBlock[],
  blockId: string,
  lineId: string,
  value: string
): TaskEditorBlock[] {
  const currentBlock = blocks.find((block) => block.id === blockId);
  const currentLine = currentBlock?.lines?.find((line) => line.id === lineId);

  if (
    !currentBlock ||
    !isTaskEditorNestedBlockType(currentBlock.type) ||
    (currentLine?.type !== "Textarea" && currentLine?.type !== "Preset") ||
    (currentLine.textareaPlaceholder ?? "") === value
  ) {
    return blocks;
  }

  return blocks.map((block) => {
    if (block.id !== blockId || !isTaskEditorNestedBlockType(block.type)) {
      return block;
    }

    return {
      ...block,
      lines: (block.lines ?? []).map((line) =>
        line.id === lineId
          ? {
              ...line,
              textareaPlaceholder: value,
            }
          : line
      ),
    };
  });
}

export function applyTypographyStyleChange(
  blocks: TaskEditorBlock[],
  blockId: string,
  lineId: string,
  style: TaskEditorTypographyStyle | null
): TaskEditorBlock[] {
  if (!style) {
    return blocks;
  }

  const currentBlock = blocks.find((block) => block.id === blockId);
  const currentLine = currentBlock?.lines?.find((line) => line.id === lineId);

  if (
    !currentBlock ||
    !isTaskEditorNestedBlockType(currentBlock.type) ||
    currentLine?.type !== "Typography" ||
    (currentLine.typographyStyle ?? "Body") === style
  ) {
    return blocks;
  }

  return blocks.map((block) => {
    if (block.id !== blockId || !isTaskEditorNestedBlockType(block.type)) {
      return block;
    }

    return {
      ...block,
      lines: (block.lines ?? []).map((line) =>
        line.id === lineId
          ? {
              ...line,
              typographyStyle: style,
            }
          : line
      ),
    };
  });
}

export function applyRemoveBlock(
  blocks: TaskEditorBlock[],
  blockId: string
): TaskEditorBlock[] {
  const targetBlock = blocks.find((block) => block.id === blockId);

  if (!targetBlock || isTaskEditorLastCardBlock(targetBlock)) {
    return blocks;
  }

  const editableBlocks = blocks.filter(
    (block) => !isTaskEditorLastCardBlock(block)
  );

  if (editableBlocks.length === 1) {
    return createEmptyTaskEditorState().blocks;
  }

  return blocks.filter((block) => block.id !== blockId);
}
